define(['services/validators', 'services/converters', './types', 'common/parameterApplications'],
    function(validators, converters, types, parameterApplications) {
        var geocodeBaseFields = {
            key: {
                validator: validators.string
            },
            position: {
                converter: converters.point,
                validator: validators.point,
                required: true,
                application: parameterApplications.PATH
            },
            spatialKeys: {
                validator: validators.bool
            },
            heading: {
                validator: validators.number
            },
            radius: {validator: validators.naturalInteger},
            protocol: {validator: validators.oneOfValue(['http', 'https'], 'protocol')}
        };

        var additionalGeocodeFields = {
            type: {
                defaultValue: types.reverseGeocode,
                visible: false,
                application: parameterApplications.PATH
            },

            allowFreeformNewline: {
                validator: validators.bool
            },

            /**
             * @class ReverseGeocode
             * @module Services
             */

            /**
             * Sets or gets the value of the option `returnSpeedLimit`.
             *
             * Allows, if possible, to receive the speed limit information at the given location.
             *
             * @method returnSpeedLimit
             * @chainable
             * @param {Boolean} [newValue] Enable or disable the feature
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {Boolean} The same service instance or the current option value if no argument was given
             */
            returnSpeedLimit: {
                validator: validators.bool
            },

            /**
             * Sets or gets the value of the option `number`.
             *
             * If a street number is sent in along with the request, the response may
             * include the side of the street (Left/Right) and also an offset position for that street number.
             *
             * @method number
             * @chainable
             * @param {String} [newValue] Street number as a string
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            number: {
                validator: validators.string
            },

            /**
             * Sets or gets the value of the option `returnRoadUse`.
             *
             * Requires to include a road use array for reversegeocodes at street level.
             *
             * @method returnRoadUse
             * @chainable
             * @param {Boolean} [newValue] Enable or disable the feature
             * @throws {TypeError} if the given argument cannot be converted into a valid value
             * @return {Boolean} The same service instance or the current option value if no argument was given
             */
            returnRoadUse: {
                validator: validators.bool
            },

            /**
             * Sets or gets the value of the option `returnMatchType`.
             *
             * If set, response will contain additional field with one of the values:
             *  * AddressPoint (exact house number location)
             *  * HouseNumberRange (interpolated house number location)
             *  * Street (street location without house number)
             *
             * @method returnMatchType
             * @chainable
             * @param {Boolean} [newValue] Enable or disable the feature
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {Boolean} The same service instance or the current option value if no argument was given
            */
            returnMatchType: {
                validator: validators.bool
            },

            /**
             * Sets or gets the value of the option `roadUse`.
             *
             * Use this option if you want to restrict the result to one or a group of the following road uses:
             * - `"LimitedAccess"`
             * - `"Arterial"`
             * - `"Terminal"`
             * - `"Ramp"`
             * - `"Rotary"`
             * - `"LocalStreet"`
             *
             * @method roadUse
             * @chainable
             * @param {String[]|String} [newValue] An array of strings or just one string with comma separated values
             * @throws {TypeError} if the given argument cannot be converted into a valid value
             * @return {String[]|String} The same service instance or the current option value if no argument was given
             */
            roadUse: {
                converter: converters.roadUse,
                validator: validators.roadUse
            },

            language: {
                validator: validators.languageCode, converter: converters.language
            },

            /**
             * The date and time to return time zone information for.
             * It allows the service to decide whether Daylight Saving Time time zone should be used or not.
             * It must conform to RFC-3339 .
             * Examples:
             *  * 2017-12-19T16:39:57
             *  * 2017-12-19T16:39:57-08:00
             *
             * @method dateTime
             * @chainable
             * @param {String} [newValue] Date to convert.
             * @throws {TypeError} if the given argument cannot be converted into a valid value
             * @return {String[]|String} The same service instance or the current option value if no argument was given
             */
            dateTime: {
                validator: validators.dateRfc3339,
                converter: converters.dateTime
            },
            view: {validator: validators.geopolView('reverseGeocoder', 'view')}
        };

        var crossStreetLookupFields = {
            type: {
                defaultValue: types.crossStreetLookup,
                visible: false,
                application: parameterApplications.PATH
            },

            limit: {
                validator: validators.limit
            },

            language: {
                validator: validators.languageCode
            }
        };

        return function(type) {
            if (type === types.reverseGeocode) {
                return Object.assign({}, geocodeBaseFields, additionalGeocodeFields);
            } else if (type === types.crossStreetLookup) {
                return Object.assign({}, geocodeBaseFields, crossStreetLookupFields);
            }
            throw new Error('Unsupported geocode type: ' + type);
        };
    });



// WEBPACK FOOTER //
// ./services/reverseGeocode/reverseGeocodeParameters.js